import entity360Service, {
  ENTITY360_MAX_CLUSTER_PAGE_SIZE,
} from "@services/entity360/entity360Service"
// `import type` bắt buộc: interface dùng trong chữ ký có decorator, với isolatedModules +
// emitDecoratorMetadata thì import thường sẽ lỗi TS1272.
import type {
  IEntity360Cluster,
  IEntity360Overview,
  ITimelineItem,
} from "@services/entity360/entity360Service"
import { action, makeAutoObservable, observable } from "mobx"

/**
 * Store của tab 360 (Feature-0193).
 *
 * Cụm được nạp LAZY và giữ riêng theo khoá: tab có tới 11 cụm, nạp hết ngay khi mở tab là bắt người dùng
 * chờ dữ liệu họ chưa chắc xem. Mỗi cụm cũng có cờ loading/lỗi riêng để một cụm hỏng không làm trắng tab.
 */
class Entity360Store {
  @observable overview?: IEntity360Overview
  @observable loadingOverview = false
  @observable errorOverview = false

  @observable clusters: Record<string, IEntity360Cluster | null> = {}
  @observable loadingClusters: Record<string, boolean> = {}
  @observable errorClusters: Record<string, boolean> = {}

  /**
   * Cụm đã nạp ở cỡ trang ĐẦY ĐỦ, không phải trang xem trước.
   *
   * Cần cờ riêng vì `clusters[key]` khác null không nói được nó đang giữ 10 dòng hay 100 — mở drawer mà
   * tin vào "đã có dữ liệu" sẽ hiện bảng cụt và vẫn gọi nó là "tất cả".
   */
  @observable fullyLoadedClusters: Record<string, boolean> = {}

  @observable timeline: ITimelineItem[] = []
  @observable timelineTotal = 0
  @observable loadingTimeline = false
  @observable errorTimeline = false

  constructor() {
    makeAutoObservable(this)
  }

  @action
  reset = (): void => {
    this.overview = undefined
    this.clusters = {}
    this.loadingClusters = {}
    this.errorClusters = {}
    this.fullyLoadedClusters = {}
    this.timeline = []
    this.timelineTotal = 0
  }

  @action
  getOverview = async (module: number, entityId: number): Promise<void> => {
    this.loadingOverview = true
    this.errorOverview = false
    try {
      this.overview = await entity360Service.getOverview(module, entityId)
    } catch {
      this.errorOverview = true
    } finally {
      this.loadingOverview = false
    }
  }

  @action
  getCluster = async (
    module: number,
    entityId: number,
    clusterKey: string,
    /** true = lấy cỡ trang đầy đủ cho bảng trong drawer, thay vì trang xem trước của thẻ. */
    isFullPage = false
  ): Promise<void> => {
    this.loadingClusters = { ...this.loadingClusters, [clusterKey]: true }
    this.errorClusters = { ...this.errorClusters, [clusterKey]: false }
    try {
      const cluster = await entity360Service.getCluster(
        module,
        entityId,
        clusterKey,
        isFullPage ? ENTITY360_MAX_CLUSTER_PAGE_SIZE : undefined
      )
      this.clusters = { ...this.clusters, [clusterKey]: cluster }
      if (isFullPage)
        this.fullyLoadedClusters = {
          ...this.fullyLoadedClusters,
          [clusterKey]: true,
        }
    } catch {
      this.errorClusters = { ...this.errorClusters, [clusterKey]: true }
    } finally {
      this.loadingClusters = { ...this.loadingClusters, [clusterKey]: false }
    }
  }

  /**
   * Nạp cụm ở cỡ trang đầy đủ cho drawer, bỏ qua nếu đã nạp đầy đủ rồi.
   *
   * Tách khỏi `getCluster` để nơi gọi không phải tự nhớ kiểm tra cờ — quên kiểm tra thì mỗi lần mở
   * drawer lại bắn thêm một request cho dữ liệu đã có.
   */
  @action
  ensureClusterFullyLoaded = async (
    module: number,
    entityId: number,
    clusterKey: string
  ): Promise<void> => {
    if (this.fullyLoadedClusters[clusterKey]) return

    await this.getCluster(module, entityId, clusterKey, true)
  }

  @action
  getTimeline = async (
    module: number,
    entityId: number,
    filter?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingTimeline = true
    this.errorTimeline = false
    try {
      const result = await entity360Service.getTimeline(module, entityId, filter)
      this.timeline = result?.items ?? []
      this.timelineTotal = result?.totalCount ?? 0
    } catch {
      this.errorTimeline = true
    } finally {
      this.loadingTimeline = false
    }
  }
}

export default Entity360Store
